import { useCallback, useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import './index.css'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { AchievementProvider, useAchievement } from './contexts/AchievementContext'
import { SettingsProvider } from './contexts/SettingsContext'
import { playSound } from './lib/sounds'
import AuthPage from './pages/AuthPage'
import Sidebar from './components/Sidebar'
import NoCompanyScreen from './components/NoCompanyScreen'
import PendingApprovalScreen from './components/PendingApprovalScreen'
import ErrorBoundary from './components/ErrorBoundary'
import ResponsiveDrawer from './components/ui/ResponsiveDrawer'
import { triggerConfetti } from './lib/confetti'
import { kanbify } from './lib/kanbifyClient'
import { appRoutes, buildSearch, isKnownAppPath } from './lib/routes'
import { identifyProductUser, resetProductAnalytics } from './lib/productAnalytics'
import { useCards } from './hooks/useKanbifyData'
import { useCompany } from './hooks/useCompanyData'
import { useProcedures } from './hooks/useProcedures'
import { useHeartbeat } from './hooks/useHeartbeat'
import AppPageHeader from './components/app/AppPageHeader'
import GlobalSearch from './components/GlobalSearch'
import CardDetail from './components/CardDetail'
import { LayoutDashboard, Clock, User, LogOut, CalendarClock, CalendarDays, Zap } from 'lucide-react'

const Leaderboard = lazy(() => import('./components/Leaderboard'))
const KpiReportsPage = lazy(() => import('./components/KpiReportsPage'))
const ProfilePage = lazy(() => import('./components/ProfilePage'))
const CompanyPage = lazy(() => import('./components/CompanyPage'))
const ProceduresView = lazy(() => import('./components/ProceduresView'))
const DepartmentsView = lazy(() => import('./components/DepartmentsView'))
const HistoryView = lazy(() => import('./components/HistoryView'))
const SchedulerView = lazy(() => import('./components/SchedulerView'))
const CalendarView = lazy(() => import('./components/CalendarView'))
const DashboardPersonalView = lazy(() => import('./components/DashboardPersonalView'))
const SuggestionsPage = lazy(() => import('./components/SuggestionsPage'))
const CockpitPage = lazy(() => import('./components/CockpitPage'))
const ArchiveView = lazy(() => import('./components/ArchiveView'))
const ChromeExtensionView = lazy(() => import('./components/ChromeExtensionView'))
const SpectatorBoard = lazy(() => import('./components/SpectatorBoard'))

const CARD_DETAIL_SELECT = `
    *,
    card_labels ( label_id, labels:label_id ( id, name, color ) ),
    card_members ( user_id, claimed_at, profiles:user_id ( id, full_name, avatar_url ) ),
    checklists ( id, name, checklist_items ( id, is_completed, is_shared ) ),
    comments ( id ),
    card_completions ( id, user_id, completed_at )
`

function LoadingFallback({ label, overlay = false }) {
    const className = overlay ? 'modal-loading' : 'panel-loading'
    return (
        <div className={className}>
            <span>{label}</span>
        </div>
    )
}

function AppContent() {
    const { t } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const { user, profile, isAdmin, canCreate, loading: authLoading, refreshProfile, sessionExpired, dismissSessionExpired } = useAuth()
    const { showAchievement } = useAchievement()

    // Heartbeat — admin cockpit foloseste user_heartbeats.last_seen_at pentru online status
    useHeartbeat()

    const [activeBoardId, setActiveBoardId] = useState(null)
    const [isSidebarOpen, setIsSidebarOpen] = useState(false)
    const [isDesktopSidebarHidden, setIsDesktopSidebarHidden] = useState(() => {
        try {
            return window.localStorage.getItem('kanbify:desktop-sidebar-hidden') === '1'
        } catch {
            return false
        }
    })
    const [showGlobalSearch, setShowGlobalSearch] = useState(false)
    const [homeDomainCounts, setHomeDomainCounts] = useState({
        procedures: { todo: 0, claimed: 0, completed: 0 },
        tasks: { todo: 0, claimed: 0, completed: 0 },
    })
    // Spectator mode: { id, name } of the employee whose board we are watching read-only.
    const [spectatorUser, setSpectatorUser] = useState(null)
    const [selectedCardFallback, setSelectedCardFallback] = useState(null)
    const [selectedCardLoading, setSelectedCardLoading] = useState(false)
    const openCardSeqRef = useRef(0)
    const boardWrapperRef = useRef(null)
    const scrollPositionsRef = useRef(new Map())

    // Cmd+K toggles global search
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault()
                setShowGlobalSearch((prev) => !prev)
            }
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [])

    // Toast on session expiry
    useEffect(() => {
        if (sessionExpired) {
            showAchievement({ type: 'error', message: t('app.sessionExpired') })
            dismissSessionExpired()
        }
    }, [sessionExpired, showAchievement, dismissSessionExpired, t])

    // Product analytics identification
    useEffect(() => {
        if (user?.id) identifyProductUser(user, profile)
        else resetProductAnalytics()
    }, [profile, user])

    // Data needed by CardDetail when ?card= is in URL.
    // We hydrate the board context for whichever board owns the selected card.
    const { cards, loading: cardsLoading, update: updateCard, remove: removeCard, fetch: fetchCards } = useCards(activeBoardId)
    // Procedures are needed only when CardDetail is open (linker UI).
    // The /procedures page owns its own data; keeping this hook inactive
    // there prevents a duplicate shim poll from causing tiny visual refreshes.
    const proceduresActive = Boolean(searchParams.get('card'))
    const { procedures } = useProcedures(proceduresActive ? profile?.company_id : null)
    const { company, members: companyMembers } = useCompany(profile?.active_company_id || profile?.company_id)
    const dashboardV2Enabled = Boolean(company?.dashboard_v2_enabled)

    const pathname = location.pathname
    const showHome = pathname === appRoutes.home
    const extensionMode = showHome && searchParams.get('extension') === '1'
    const dashboardDomain = searchParams.get('tab') === 'tasks' ? 'tasks' : 'procedures'
    const viewScrollKey = showHome ? `${pathname}?tab=${dashboardDomain}` : pathname
    const showLeaderboard = pathname === appRoutes.leaderboard
    const showKpis = pathname === appRoutes.kpis
    const showProcedures = pathname === appRoutes.procedures
    const showDepartments = pathname === appRoutes.departments
    const showHistory = pathname === appRoutes.history
    const showScheduler = pathname === appRoutes.scheduler
    const showCalendar = pathname === appRoutes.calendar
    const showSuggestions = pathname === appRoutes.suggestions
    const showCockpit = pathname === appRoutes.cockpit
    const showArchive = pathname === appRoutes.archive
    const showChromeExtension = pathname === appRoutes.chromeExtension
    const showProfile = searchParams.get('modal') === 'profile'
    const showCompany = searchParams.get('modal') === 'company'
    const selectedCardId = searchParams.get('card')
    // Card details always use the authenticated member's normal edit rights,
    // including when opened from spectator or archive surfaces.
    const selectedCardViewContext = useMemo(() => ({
        zone: searchParams.get('vz') || null,
        label: searchParams.get('vl') || null,
        focus: searchParams.get('focus') || null,
    }), [searchParams])
    // Only admins/managers (and super-admins, folded into isAdmin) may watch an employee's board.
    const canSpectate = isAdmin || profile?.role === 'manager' || profile?.role === 'admin'

    useEffect(() => {
        const wrapper = boardWrapperRef.current
        if (!wrapper) return undefined
        const scrollPositions = scrollPositionsRef.current
        const key = viewScrollKey
        let frame = null
        const savedTop = scrollPositions.get(key)
        if (typeof savedTop === 'number') {
            frame = requestAnimationFrame(() => {
                wrapper.scrollTop = savedTop
            })
        }
        const handleScroll = () => {
            scrollPositions.set(key, wrapper.scrollTop)
        }
        wrapper.addEventListener('scroll', handleScroll, { passive: true })
        return () => {
            if (frame) cancelAnimationFrame(frame)
            scrollPositions.set(key, wrapper.scrollTop)
            wrapper.removeEventListener('scroll', handleScroll)
        }
    }, [viewScrollKey])

    /* ---------- Routing guards ---------- */

    useEffect(() => {
        if (authLoading || !user) return

        if (profile && !profile.company_id) {
            if (pathname !== appRoutes.onboarding) {
                navigate({ pathname: appRoutes.onboarding, search: location.search }, { replace: true })
            }
            return
        }

        if (profile?.company_id) {
            // Default landing → /home (Dashboard Personal)
            if (pathname === '/' || pathname === appRoutes.onboarding) {
                navigate({ pathname: appRoutes.home, search: location.search }, { replace: true })
                return
            }
            // Any unknown path (including legacy /boards/X, /my-tasks, /dashboard) → /home
            if (!isKnownAppPath(pathname)) {
                navigate({ pathname: appRoutes.home, search: location.search }, { replace: true })
            }
        }
    }, [authLoading, dashboardV2Enabled, location.search, navigate, pathname, profile, user])

    /* ---------- Search params helpers ---------- */

    const updateSearchState = useCallback((updates = {}, removals = [], options = {}) => {
        const nextSearch = buildSearch(searchParams, updates, removals)
        const currentSearch = searchParams.toString()
        const normalizedCurrent = currentSearch ? `?${currentSearch}` : ''
        if (nextSearch === normalizedCurrent) return
        setSearchParams(nextSearch, options)
    }, [searchParams, setSearchParams])

    const closeSidebar = useCallback(() => setIsSidebarOpen(false), [])
    const hideDesktopSidebar = useCallback(() => {
        setIsDesktopSidebarHidden(true)
        try { window.localStorage.setItem('kanbify:desktop-sidebar-hidden', '1') } catch { /* optional preference */ }
    }, [])
    const openShellSidebar = useCallback(() => {
        if (window.matchMedia('(max-width: 900px)').matches) {
            setIsSidebarOpen(true)
            return
        }
        setIsDesktopSidebarHidden(false)
        try { window.localStorage.removeItem('kanbify:desktop-sidebar-hidden') } catch { /* optional preference */ }
    }, [])
    const closeCardRoute = useCallback(() => {
        const savedTop = scrollPositionsRef.current.get(viewScrollKey)
        setSelectedCardFallback(null)
        setSelectedCardLoading(false)
        updateSearchState({}, ['card', 'ro', 'vz', 'vl', 'focus'], { replace: true })
        if (typeof savedTop === 'number') {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (boardWrapperRef.current) boardWrapperRef.current.scrollTop = savedTop
                })
            })
        }
    }, [updateSearchState, viewScrollKey])
    const openModal = (modal) => updateSearchState({ modal }, ['card'], { replace: true })
    const closeModal = (modal) => {
        if (searchParams.get('modal') !== modal) return
        updateSearchState({}, ['modal'], { replace: true })
    }

    /* ---------- Card opening (modal-only, no board navigation) ---------- */

    const resolveBoardIdForCard = useCallback(async (cardId) => {
        const { data: card } = await kanbify
            .from('cards')
            .select('list_id')
            .eq('id', cardId)
            .maybeSingle()
        if (!card?.list_id) return null
        const { data: list } = await kanbify
            .from('lists')
            .select('board_id')
            .eq('id', card.list_id)
            .maybeSingle()
        return list?.board_id || null
    }, [])

    const fetchCardForModal = useCallback(async (cardId) => {
        if (!cardId) return null
        const { data, error } = await kanbify
            .from('cards')
            .select(CARD_DETAIL_SELECT)
            .eq('id', cardId)
            .maybeSingle()
        if (error) return null
        return data || null
    }, [])

    const openCardRoute = useCallback(async (cardId, options = {}) => {
        if (!cardId) return
        if (boardWrapperRef.current) {
            scrollPositionsRef.current.set(viewScrollKey, boardWrapperRef.current.scrollTop)
        }
        if (
            selectedCardId === cardId
            && !selectedCardLoading
            && (selectedCardFallback?.id === cardId || cards.some((card) => card.id === cardId))
        ) return
        const normalizedOptions = typeof options === 'string' ? { boardId: options } : (options || {})
        const seq = ++openCardSeqRef.current
        let existingCard = normalizedOptions.card || cards.find((card) => card.id === cardId) || null
        const shouldPreloadBeforeOpen = Boolean(normalizedOptions.preload)

        if (shouldPreloadBeforeOpen) {
            // Open the modal IMMEDIATELY (loading state) for instant feedback, then
            // resolve board + card in the background and fill it in. Awaiting both
            // fetches BEFORE opening made the click feel dead on slower links (cPanel
            // shim) — users clicked several times and waited for the card to appear.
            // (Teo, 2026-05-22 — feedback #2 „intra greu / trebuie mai multe click-uri".)
            setSelectedCardLoading(!existingCard)
            if (existingCard) setSelectedCardFallback(existingCard)
            updateSearchState({
                card: cardId,
                ro: normalizedOptions.readOnly ? '1' : null,
                vz: normalizedOptions.viewZone || null,
                vl: normalizedOptions.viewLabel || null,
                focus: normalizedOptions.focus || null,
            }, ['modal'], { replace: normalizedOptions.replace ?? false })
            closeSidebar()

            const [targetBoardId, preloadedCard] = await Promise.all([
                normalizedOptions.boardId ? Promise.resolve(normalizedOptions.boardId) : resolveBoardIdForCard(cardId),
                existingCard ? Promise.resolve(existingCard) : fetchCardForModal(cardId),
            ])
            if (seq !== openCardSeqRef.current) return
            if (preloadedCard) {
                setSelectedCardFallback(preloadedCard)
            } else if (!targetBoardId) {
                setSelectedCardLoading(false)
                showAchievement({ type: 'error', message: t('errors.cardNotFound') })
                return
            }
            if (targetBoardId && activeBoardId !== targetBoardId) {
                setActiveBoardId(targetBoardId)
            }
            setSelectedCardLoading(false)
            return
        }

        setSelectedCardLoading(!existingCard)
        if (existingCard) setSelectedCardFallback(existingCard)
        updateSearchState({
            card: cardId,
            ro: normalizedOptions.readOnly ? '1' : null,
            vz: normalizedOptions.viewZone || null,
            vl: normalizedOptions.viewLabel || null,
            focus: normalizedOptions.focus || null,
        }, ['modal'], { replace: normalizedOptions.replace ?? false })
        closeSidebar()

        const [targetBoardId, directCard] = await Promise.all([
            normalizedOptions.boardId ? Promise.resolve(normalizedOptions.boardId) : resolveBoardIdForCard(cardId),
            existingCard ? Promise.resolve(existingCard) : fetchCardForModal(cardId),
        ])

        if (seq !== openCardSeqRef.current) return
        if (directCard) {
            setSelectedCardFallback((current) => (
                current?.id === directCard.id && JSON.stringify(current) === JSON.stringify(directCard)
                    ? current
                    : directCard
            ))
        }
        if (!targetBoardId && !directCard) {
            setSelectedCardLoading(false)
            showAchievement({ type: 'error', message: t('errors.cardNotFound') })
            return
        }
        if (targetBoardId && activeBoardId !== targetBoardId) {
            setActiveBoardId(targetBoardId)
        }
        setSelectedCardLoading(false)
    }, [activeBoardId, cards, closeSidebar, fetchCardForModal, resolveBoardIdForCard, selectedCardFallback?.id, selectedCardId, selectedCardLoading, showAchievement, t, updateSearchState, viewScrollKey])

    // When landing on /home?card=X (e.g. reload), make sure the card's board
    // is loaded so CardDetail has data.
    useEffect(() => {
        if (!selectedCardId) return undefined
        if (!profile?.company_id) return undefined
        let cancelled = false
        ;(async () => {
            const hasCurrentFallback = selectedCardFallback?.id === selectedCardId
            const hasCurrentBoardCard = cards.some((card) => card.id === selectedCardId)
            const [resolvedBoardId, directCard] = await Promise.all([
                resolveBoardIdForCard(selectedCardId),
                hasCurrentFallback || hasCurrentBoardCard ? Promise.resolve(null) : fetchCardForModal(selectedCardId),
            ])
            if (cancelled) return
            if (directCard) setSelectedCardFallback(directCard)
            if (resolvedBoardId) {
                setActiveBoardId((current) => current === resolvedBoardId ? current : resolvedBoardId)
            }
            setSelectedCardLoading(false)
        })()
        return () => { cancelled = true }
    }, [cards, fetchCardForModal, profile?.company_id, resolveBoardIdForCard, selectedCardFallback?.id, selectedCardId])

    /* ---------- Derived ---------- */

    const selectedCardData = useMemo(() => {
        if (!selectedCardId) return null
        // Prefer the LIVE card from the polled list so the open modal reflects
        // updates (timer started elsewhere / auto-chain, completion, etc.) without
        // a manual refresh. The frozen snapshot is only a fallback for cards not
        // yet in the list (opened from search/deep-link). (Teo 2026-06-04)
        return cards.find((card) => card.id === selectedCardId)
            || (selectedCardFallback?.id === selectedCardId ? selectedCardFallback : null)
            || null
    }, [cards, selectedCardFallback, selectedCardId])

    const cardDetailAllCards = useMemo(() => {
        if (!selectedCardFallback || cards.some((card) => card.id === selectedCardFallback.id)) return cards
        return [...cards, selectedCardFallback]
    }, [cards, selectedCardFallback])

    /* ---------- Card mutation handlers ---------- */

    const handleUpdateCard = async (cardId, updates) => {
        const { card: previousCard, error: prevError } = await (async () => {
            const { data, error } = await kanbify.from('cards').select('*').eq('id', cardId).maybeSingle()
            return { card: data, error }
        })()
        if (prevError) showAchievement({ type: 'error', message: 'Nu am putut citi cardul' })

        const result = await updateCard(cardId, updates)
        if (result?.success === false) {
            showAchievement({ type: 'error', message: result.error })
            return
        }
        setSelectedCardFallback((current) => (
            current?.id === cardId ? { ...current, ...updates } : current
        ))

        // Points are now awarded by DB trigger fn_award_card_completion based on
        // cards.point_value (set explicitly or inherited from procedure). No more
        // flat 10p from client. The trigger handles assigned task members.
        if (updates.is_completed === true && previousCard && !previousCard.is_completed) {
            triggerConfetti()
            playSound('complete')
            // Brief delay to let the trigger commit before refetching the profile.
            setTimeout(() => refreshProfile?.(), 300)
        }
    }

    // KPI sidebar refresh (Teo 2026-05-26): dashboard's KPI modal writes
    // completion_payload directly via kanbify.update; CardDetail (if open)
    // would stay stale until next 15s realtime poll. Listen for the patch
    // event and merge into selectedCardFallback so the right sidebar reflects
    // the new KPI values immediately after submit.
    useEffect(() => {
        const handler = (e) => {
            const { cardId, patch } = e?.detail || {}
            if (!cardId || !patch) return
            setSelectedCardFallback((current) => (
                current?.id === cardId ? { ...current, ...patch } : current
            ))
        }
        window.addEventListener('kanbify:card-payload-patch', handler)
        return () => window.removeEventListener('kanbify:card-payload-patch', handler)
    }, [])

    const handleDeleteCard = async (cardId) => {
        const result = await removeCard(cardId)
        if (result?.success === false) {
            showAchievement({ type: 'error', message: result.error })
            return
        }
        // Live update: drop the archived card from the dashboard instantly
        // (optimistic) + trigger a refetch so every open view stays in sync
        // without a manual refresh.
        window.dispatchEvent(new CustomEvent('kanbify:dashboard-card-state', { detail: { type: 'removed', cardId } }))
        window.dispatchEvent(new CustomEvent('kanbify:dashboard-refetch'))
        closeCardRoute()
        setSelectedCardFallback(null)
    }

    /* ---------- Navigation handlers (sidebar items) ---------- */

    const openHome = () => { navigate({ pathname: appRoutes.home, search: '?tab=procedures' }); closeSidebar() }
    const openTasks = () => { navigate({ pathname: appRoutes.home, search: '?tab=tasks' }); closeSidebar() }
    const openLeaderboard = () => { navigate({ pathname: appRoutes.leaderboard }); closeSidebar() }
    const openKpis = () => { navigate({ pathname: appRoutes.kpis }); closeSidebar() }
    const openProcedures = () => { navigate({ pathname: appRoutes.procedures }); closeSidebar() }
    const openDepartments = () => { navigate({ pathname: appRoutes.departments }); closeSidebar() }
    const openHistory = () => { navigate({ pathname: appRoutes.history }); closeSidebar() }
    const openScheduler = () => { navigate({ pathname: appRoutes.scheduler }); closeSidebar() }
    const openCalendar = () => { navigate({ pathname: appRoutes.calendar }); closeSidebar() }
    const openSuggestions = () => { navigate({ pathname: appRoutes.suggestions }); closeSidebar() }
    const openCockpit = () => { navigate({ pathname: appRoutes.cockpit }); closeSidebar() }
    const openArchive = () => { navigate({ pathname: appRoutes.archive }); closeSidebar() }
    const openChromeExtension = () => { navigate({ pathname: appRoutes.chromeExtension }); closeSidebar() }
    const openProfile = () => { openModal('profile'); closeSidebar() }
    const openCompany = () => { openModal('company'); closeSidebar() }

    /* ---------- Render ---------- */

    const renderShellSidebar = (mobile = false, canHideDesktop = true) => (
        <Sidebar
            onShowLeaderboard={openLeaderboard}
            showLeaderboard={showLeaderboard}
            onShowKpis={profile?.company_id ? openKpis : undefined}
            showKpis={showKpis}
            onShowProcedures={openProcedures}
            showProcedures={showProcedures}
            onShowDepartments={openDepartments}
            showDepartments={showDepartments}
            onShowHistory={openHistory}
            showHistory={showHistory}
            onShowScheduler={openScheduler}
            showScheduler={showScheduler}
            onShowCalendar={openCalendar}
            showCalendar={showCalendar}
            onShowHome={profile?.company_id ? openHome : undefined}
            showHome={showHome && dashboardDomain === 'procedures'}
            onShowTasks={profile?.company_id ? openTasks : undefined}
            showTasks={showHome && dashboardDomain === 'tasks'}
            onShowSuggestions={openSuggestions}
            showSuggestions={showSuggestions}
            onShowCockpit={profile?.company_id ? openCockpit : undefined}
            showCockpit={showCockpit}
            onShowArchive={profile?.company_id ? openArchive : undefined}
            showArchive={showArchive}
            onShowChromeExtension={openChromeExtension}
            showChromeExtension={showChromeExtension}
            dashboardV2Enabled={dashboardV2Enabled}
            homeDomainCounts={homeDomainCounts}
            onShowProfile={openProfile}
            onShowCompany={profile?.company_id ? openCompany : undefined}
            onCloseMobile={mobile ? closeSidebar : undefined}
            onHideDesktop={!mobile && canHideDesktop ? hideDesktopSidebar : undefined}
        />
    )

    if (authLoading) {
        return (
            <div className="auth-page">
                <div className="auth-bg" />
                <LoadingFallback label={t('common.loading')} />
            </div>
        )
    }

    if (!user) return <AuthPage />

    if (user && profile && profile.is_active === false) {
        return <PendingApprovalScreen />
    }

    if (user && profile && !profile.company_id) {
        return (
            <div className="app-shell">
                <ResponsiveDrawer open={isSidebarOpen} onClose={closeSidebar}>
                    {isSidebarOpen ? renderShellSidebar(true) : null}
                </ResponsiveDrawer>
                <div className="app-sidebar-desktop">{renderShellSidebar(false, false)}</div>
                <div className="main-content">
                    <NoCompanyScreen profile={profile} onJoined={refreshProfile} />
                </div>
            </div>
        )
    }

    return (
        <ErrorBoundary>
            <div className={`app-shell${extensionMode ? ' extension-shell' : ''}${isDesktopSidebarHidden ? ' is-sidebar-hidden' : ''}`}>
                <ResponsiveDrawer open={isSidebarOpen} onClose={closeSidebar}>
                    {isSidebarOpen ? renderShellSidebar(true) : null}
                </ResponsiveDrawer>

                <div className="app-sidebar-desktop">
                    {renderShellSidebar(false)}
                </div>

                <div className="main-content">
                    <AppPageHeader
                        showLeaderboard={showLeaderboard}
                        showKpis={showKpis}
                        showProcedures={showProcedures}
                        showDepartments={showDepartments}
                        showHistory={showHistory}
                        showScheduler={showScheduler}
                        showHome={showHome}
                        showSuggestions={showSuggestions}
                        onOpenSidebar={openShellSidebar}
                        onOpenCard={(cardId, options = {}) => openCardRoute(cardId, { preload: true, ...options })}
                        onOpenGlobalSearch={() => setShowGlobalSearch(true)}
                    />

                    <div className="board-wrapper" ref={boardWrapperRef}>
	                        {showHome ? (
	                            <Suspense fallback={<LoadingFallback label={t('app.loading.dashboard')} />}>
		                                <DashboardPersonalView
                                            extensionMode={extensionMode}
                                            onOpenCard={(cardId, options = {}) => openCardRoute(cardId, { preload: true, ...options })}
                                            activeDomain={dashboardDomain}
                                            onDomainChange={(domain) => {
                                                setSearchParams(buildSearch(searchParams, { tab: domain }))
                                            }}
                                            onDomainCountsChange={setHomeDomainCounts}
                                            canSpectate={canSpectate}
                                            companyMembers={companyMembers}
                                        />
	                            </Suspense>
                        ) : showSuggestions ? (
                            <Suspense fallback={<LoadingFallback label={t('app.loading.suggestions')} />}>
                                <SuggestionsPage />
                            </Suspense>
                        ) : showCockpit ? (
	                            <Suspense fallback={<LoadingFallback label={t('app.loading.cockpit')} />}>
	                                <CockpitPage
	                                    onOpenCard={(cardId, options = {}) => openCardRoute(cardId, { preload: true, ...options })}
	                                />
	                            </Suspense>
                        ) : showArchive ? (
                            <Suspense fallback={<LoadingFallback label={t('app.loading.archive')} />}>
                                <ArchiveView onOpenCard={(cardId) => openCardRoute(cardId, { preload: true, readOnly: true })} />
                            </Suspense>
                        ) : showChromeExtension ? (
                            <Suspense fallback={<LoadingFallback label={t('common.loading')} />}>
                                <ChromeExtensionView />
                            </Suspense>
                        ) : showCalendar ? (
                            <Suspense fallback={<LoadingFallback label={t('app.loading.calendar')} />}>
                                <CalendarView onOpenCard={(cardId) => openCardRoute(cardId, { preload: true })} />
                            </Suspense>
                        ) : showHistory ? (
                            <Suspense fallback={<LoadingFallback label={t('app.loading.history')} />}>
                                <HistoryView
                                    companyId={profile?.company_id}
                                    companyMembers={companyMembers}
                                    onOpenCard={openCardRoute}
                                />
                            </Suspense>
                        ) : showScheduler ? (
                            <Suspense fallback={<LoadingFallback label={t('app.loading.scheduler')} />}>
                                <SchedulerView
                                    companyId={profile?.company_id}
                                    onOpenCard={(cardId, boardId) => openCardRoute(cardId, { preload: true, boardId })}
                                />
                            </Suspense>
                        ) : showProcedures ? (
                            <Suspense fallback={<LoadingFallback label={t('app.loading.procedures')} />}>
                                <ProceduresView key={profile?.active_company_id || profile?.company_id || 'no-company'} />
                            </Suspense>
                        ) : showDepartments ? (
                            <Suspense fallback={<LoadingFallback label={t('app.loading.departments')} />}>
                                <DepartmentsView />
                            </Suspense>
                        ) : showKpis ? (
                            <Suspense fallback={<LoadingFallback label={t('app.loading.kpis')} />}>
                                <KpiReportsPage />
                            </Suspense>
                        ) : showLeaderboard ? (
                            <Suspense fallback={<LoadingFallback label={t('app.loading.leaderboard')} />}>
                                <Leaderboard />
                            </Suspense>
                        ) : (
                            <LoadingFallback label={t('common.loading')} />
                        )}
                    </div>
                </div>

                {/* Card detail modal — overlays whatever view is active */}
                {selectedCardId && !selectedCardData && (cardsLoading || selectedCardLoading) ? (
                    <LoadingFallback label={t('app.loading.details')} overlay />
                ) : null}

                {selectedCardData ? (
                    <CardDetail
                        key={selectedCardData.id}
                        card={selectedCardData}
                        allCards={cardDetailAllCards}
                        onClose={closeCardRoute}
                        onUpdate={handleUpdateCard}
                        onDelete={handleDeleteCard}
                        onRefreshCards={fetchCards}
                        isAdmin={isAdmin}
                        canCreate={canCreate}
                        procedures={procedures}
                        viewContext={selectedCardViewContext}
                        extensionMode={extensionMode}
                    />
                ) : null}

                {/* Spectator board — read-only view of an employee's day (CEO oversight) */}
                {spectatorUser ? (
                    <Suspense fallback={<LoadingFallback label={t('app.loading.employeeBoard')} overlay />}>
                        <SpectatorBoard
                            userId={spectatorUser.id}
                            userName={spectatorUser.name}
                            companyId={profile?.company_id}
                            onClose={() => setSpectatorUser(null)}
                            onOpenCard={(cardId) => openCardRoute(cardId, { preload: true, readOnly: true })}
                        />
                    </Suspense>
                ) : null}

                {/* Profile / Company modals (?modal= param) */}
                {showProfile && profile ? (
                    <Suspense fallback={<LoadingFallback label={t('app.loading.profile')} overlay />}>
                        <ProfilePage onClose={() => closeModal('profile')} />
                    </Suspense>
                ) : null}

                {showCompany && profile?.company_id ? (
                    <Suspense fallback={<LoadingFallback label={t('app.loading.company')} overlay />}>
                        <CompanyPage
                            onClose={() => closeModal('company')}
                            onOpenCard={(cardId, options = {}) => openCardRoute(cardId, { preload: true, ...options })}
                        />
                    </Suspense>
                ) : null}

                {/* Cmd+K global search */}
                <GlobalSearch
                    companyId={profile?.company_id}
                    isOpen={showGlobalSearch}
                    onClose={() => setShowGlobalSearch(false)}
                    onSelectCard={(cardId) => openCardRoute(cardId, { preload: true })}
                    onSelectBoard={() => { /* boards no longer navigable as a view */ }}
                    onSelectSuggestion={() => { navigate({ pathname: appRoutes.suggestions }) }}
                    onSelectMember={canSpectate ? (member) => { setSpectatorUser({ id: member.id, name: member.full_name || member.email }); setShowGlobalSearch(false) } : undefined}
                    actions={[
                        ...(profile?.company_id ? [{
                            id: 'open-home',
                            label: t('app.actions.openHome'),
                            icon: LayoutDashboard,
                            run: openHome,
                        }] : []),
                        { id: 'open-suggestions', label: t('app.actions.openSuggestions'), icon: Zap, run: openSuggestions },
                        { id: 'open-history', label: t('app.actions.openHistory'), icon: Clock, run: openHistory },
                        { id: 'open-scheduler', label: t('app.actions.openScheduler'), icon: CalendarClock, run: openScheduler },
                        { id: 'open-calendar', label: t('app.actions.openCalendar'), icon: CalendarDays, run: openCalendar },
                        { id: 'open-profile', label: t('app.actions.openProfile'), icon: User, run: openProfile },
                        {
                            id: 'sign-out',
                            label: t('common.logout'),
                            icon: LogOut,
                            run: async () => {
                                const { error } = await kanbify.auth.signOut()
                                if (error) showAchievement({ type: 'error', message: error.message })
                            },
                        },
                    ]}
                />

            </div>
        </ErrorBoundary>
    )
}

export default function App() {
    return (
        <SettingsProvider>
            <AchievementProvider>
                <AuthProvider>
                    <AppContent />
                </AuthProvider>
            </AchievementProvider>
        </SettingsProvider>
    )
}
